import { format } from 'date-fns';

/** Quick Meeting limits and cadences (API-CONTRACT §18.3–§18.5). */
export const QM_LIMITS = {
  NOTICE_VERSION: 'QM-NOTICE-1',
  MAX_MINUTES: 115,
  WARN_MINUTES: 105,
  /** The server discards anything shorter (§18.4); the shell discards first, with copy. */
  MIN_RECORDING_MS: 10_000,
  HEARTBEAT_MS: 60_000,
  TITLE_MAX: 120,
} as const;

export const QM_POLL = {
  STATUS_MS: 60_000,
  STATUS_ACTIVE_MS: 10_000,
  NEXT_MS: 60_000,
  PREFS_STALE_MS: 60_000,
  /** 5xx back-off ceiling (§18.4). */
  MAX_BACKOFF_MS: 5 * 60_000,
  CHIP_TICK_MS: 30_000,
  PILL_TICK_MS: 1_000,
  /** How often another tab's recording is checked against its Web Lock. */
  REMOTE_LIVENESS_MS: 10_000,
} as const;

export const QM_TIMEOUTS = {
  /** From `engine.start()` resolving to its status reaching the bridge. */
  ENGINE_SETTLE_MS: 15_000,
  /** Sign-out waits this long for a save before forcing the choice dialog. */
  SIGN_OUT_SAVE_MS: 120_000,
  /** Sign-out waits this long for another tab to stop its recording. */
  SIGN_OUT_REMOTE_MS: 60_000,
  /** The worklet learns of a pause by message; the stop chime waits out that hop. */
  STOP_CHIME_GUARD_MS: 100,
} as const;

/** The pill's mic-level meter and "can't hear any speech" check (dBFS of the RMS level). */
export const QM_MIC_METER = {
  TICK_MS: 100,
  /** Reduced motion: the meter changes at most this often. */
  REDUCED_MOTION_TICK_MS: 600,
  SEGMENTS: 4,
  METER_MIN_DB: -60,
  METER_MAX_DB: -20,
  DECAY_DB_PER_TICK: 6,
  /** Background = a low percentile of the last ~3 s: the gaps between words, or a steady fan. */
  FLOOR_WINDOW_TICKS: 30,
  FLOOR_PERCENTILE: 0.1,
  /** Ticks gathered before judging, so the first reading never becomes the background. */
  WARMUP_TICKS: 10,
  SPEECH_OVER_FLOOR_DB: 8,
  SPEECH_MIN_DB: -55,
  /** A close earphone mic can keep the floor high; loud still counts if the level moves like speech. */
  SPEECH_LOUD_DB: -35,
  /** 10th–90th percentile spread over the window: a steady hum stays under it, speech clears it. */
  LOUD_MIN_SPREAD_DB: 6,
  SPREAD_HIGH_PERCENTILE: 0.9,
  /** Consecutive loud ticks that count as speech, so a click or knock does not. */
  SPEECH_MIN_TICKS: 2,
  /** Long enough that a listener sitting quiet through someone else's turn isn't flagged. */
  QUIET_MS: 25_000,
  /** A tick later than this (a throttled background tab) restarts the quiet clock. */
  TICK_GAP_MS: 1_000,
} as const;

/** The mic picker, auto-switch and the swap log sent with `complete` (API-CONTRACT §18.13). */
export const QM_MIC = {
  /** Picker value for "System default": Chrome's own id for the OS-default alias. */
  SYSTEM_DEFAULT: 'default',
  /** Plugging one device in fires several `devicechange`s; read the list once they settle. */
  DEVICE_CHANGE_DEBOUNCE_MS: 500,
  NOTICE_MS: 6_000,
  UNDO_MS: 10_000,
  PROMPT_MS: 15_000,
  /** calendar-api's `complete` limits; past them the request would fail validation and lose the save. */
  MAX_SWAPS: 100,
  SWAP_LABEL_MAX: 200,
} as const;

/** Per user, not per workspace: a mic belongs to the person's machine. Device ids are per origin anyway. */
export const micStorageKey = (userId: string) => `clarix.qm.mic.${userId}`;

/** `complete` is fire-and-forget in the engine, so the adapter retries it: with parts, then without. */
export const COMPLETE_RETRY_DELAYS_MS = [0, 2_000, 8_000] as const;

/** A start that failed after the row exists must not leave it `recording`, so its abort retries. */
export const ABORT_RETRY_DELAYS_MS = [0, 1_000, 4_000] as const;

/** 503 from abort / delete when S3 refuses to delete the audio (§18.8); the row is left as it was. */
export const DELETE_UNAVAILABLE_CODE = 'RECORDING_DELETE_UNAVAILABLE';

/** Lost-signal and processing clocks (API-CONTRACT §18.2, §18.8). */
export const QM_RECOVERY = {
  /** Three missed heartbeats: no tab anywhere drives a `recording` row. */
  ORPHAN_AFTER_MS: 3 * 60_000,
  /** A status read older than this (a hidden tab, a backed-off poll) never flags an orphan. */
  STATUS_FRESH_MS: 90_000,
  /** A save waiting for the network tries again after this, doubling up to the cap, and on reconnect. */
  NETWORK_RETRY_MS: 30_000,
  NETWORK_RETRY_MAX_MS: 5 * 60_000,
  /** The server retries a stuck stage at 15 min; past this the row says it is taking longer. */
  SLOW_PROCESSING_MS: 10 * 60_000,
  /** How often the orphan and slow-processing labels are re-read. */
  CLOCK_TICK_MS: 30_000,
  /** The audio clock fell this far behind the wall clock: the computer slept or froze. */
  SLEEP_GAP_MS: 30_000,
  SLEEP_CHECK_MS: 5_000,
  /** The audio clock trails the wall clock by this much without a sleep: the counter re-bases on it. */
  AUDIO_LAG_MS: 1_000,
  /** Without an audio clock only a tick gap tells; past the 1-minute throttle of a hidden tab. */
  SLEEP_TICK_GAP_MS: 90_000,
} as const;

export const CHIP = {
  /** Inside this window the chip counts down and shows the room. */
  SOON_MS: 15 * 60_000,
  /** From here the chip goes solid and offers Join (contract §8 #19: five minutes before). */
  IMMINENT_MIN: 5,
  LIST_LIMIT: 3,
  /** Faces in a stack before the rest fold into a "+N". */
  FACES_MAX: 3,
} as const;

/** Window signals from the calendar remote (§18.7); KEEP THE NAMES IN SYNC with apps/calendar/src/lib/shell-signals.ts. */
export const CALENDAR_SIGNAL = {
  RECORDINGS_CHANGED: 'calendar:recordings-changed',
  EVENTS_CHANGED: 'calendar:events-changed',
} as const;

/** User- and workspace-scoped: SINGLE_HOST mode shares one origin across both. */
export function quickMeetingScope(userId: string, slug: string): string {
  return `clarix-quick-meeting:${userId}:${slug}`;
}

/** The calendar remote's routes the shell links to (its `CALENDAR_INTENT` `event` and `new`). */
export const CALENDAR_PATH = {
  HOME: '/calendar',
  /** "More options": the full form with what the top bar filled in (CALENDAR_NEW_PARAM in the remote). */
  newMeeting: (draft: { title: string; date: string; time: string; minutes: number }) =>
    `/calendar?${new URLSearchParams({
      new: 'meeting',
      new_title: draft.title,
      new_date: draft.date,
      new_time: draft.time,
      new_min: String(draft.minutes),
    }).toString()}`,
  RECORDINGS: '/calendar/recordings',
  recording: (id: string) => `/calendar/recordings/${encodeURIComponent(id)}`,
  event: (id: string, startIso: string) =>
    `/calendar?${new URLSearchParams({
      date: format(new Date(startIso), 'yyyy-MM-dd'),
      event: id,
    }).toString()}`,
} as const;

export const ACTIVE_RECORDING_STATUSES = ['normalizing', 'queued', 'transcribing', 'summarizing'] as const;
